import type {AuthFormSubmitSuccessCallback, VoidFunction} from '@Components/login-page/login-page.types';
import type {LOGIN_OPTIONS} from '@Libraries/login-signup-library';

/**
 * The two surfaces that render the auth form column. The login page sizes itself against the viewport and
 * widens on a widescreen display; the authenticate modal is a fixed 463px column whatever the viewport, so
 * it opts out of the widescreen treatment entirely.
 */
export enum AuthSurface {
  PAGE = 'page',
  MODAL = 'modal',
}

export const SMALL_SCREEN_HEIGHT_BREAKPOINT = 1000;

/**
 * What the modal's bottom sheet needs to draw a panel's header bar itself: on mobile the back control and
 * title sit in the sheet's full-bleed nav, above the padded content this component
 * renders, so the column cannot draw it in place.
 */
export interface SheetHeading {
  title: string;
  hasBackControl: boolean;
  onBack: VoidFunction;
}

export interface LoginPageContentProps {
  onAuthTypeChangeCallback: VoidFunction;
  onPanelChange: VoidFunction;
  loginOptionsMode: LOGIN_OPTIONS;
  isUserJoiningTeam: boolean;
  teamName?: string;
  redirectUrl?: string;
  surface?: AuthSurface;
  /**
   * Replaces the log-in and sign-up panel titles. The modal is opened from a dozen places that each name
   * the thing the user was doing ("Log in or Sign up to save your design"); every other panel keeps its own
   * title, because those steps mean the same thing wherever the flow started.
   */
  heading?: string;
  /**
   * Called once the user is authenticated. The login page leaves this unset — its forms navigate on their
   * own — so supplying it is what makes a surface stay put and handle success itself.
   */
  onAuthSuccess?: AuthFormSubmitSuccessCallback;
  /** Paired with `onAuthSuccess`: an SSO provider answered, but not with a usable account. */
  onAuthError?: VoidFunction;
  /** Modal surface only: fired whenever the active panel's header changes while rendered as a phone sheet. */
  onSheetHeadingChange?: (heading: SheetHeading) => void;
}
